// Pure helpers for receiving material and entering vendor bills.
//
// Rate conventions used here:
//  - Bill rates are GST-inclusive and freight-free (freight is a separate bill-level line).
//  - The rate library stores a base rate per quote (or per option) in the quote's own GST mode,
//    normally per "library unit". For pack-labelled varieties (e.g. "50 Kg") that unit is the pack.
//  - An order is received and billed in its own unit (e.g. Kgs). "perOrderUnit" below means
//    rupees per one order unit, GST-inclusive.
import {
  expandQuoteOptionsFromTemplate,
  MaterialOrderReceipt,
  MaterialProcurementOrder,
  MaterialRateItem,
  normalizeUnit,
  quoteBaseRate,
  quoteOptionBaseRate,
  sameUnit,
} from '../pages/OfficeEstimationPage';
import { num } from './estimationUtils';

type Quote = NonNullable<MaterialRateItem['quotes']>[number];
type Option = NonNullable<Quote['quoteOptions']>[number];

export const normVendor = (value?: string) => String(value || '').trim().toLowerCase();

/**
 * A receipt is billed once the office has attached a bill (billId). Receipts saved by the older flow
 * with a bill number or bill document are already billed. A bill number noted at the gate
 * (billDetailsPending) is NOT billed until the office adds the rates and totals.
 */
export const isReceiptBilled = (receipt: MaterialOrderReceipt) =>
  !!receipt.billId || (!receipt.billDetailsPending && (receipt.documentType === 'bill' || !!receipt.billNumber));

export const orderReceivedQty = (order: Pick<MaterialProcurementOrder, 'receipts'>) =>
  (order.receipts || []).reduce((sum, receipt) => sum + num(receipt.qty), 0);

/** Balance still expected. Zero once received in full, over-received, or closed short. */
export const orderPendingQty = (order: Pick<MaterialProcurementOrder, 'receipts' | 'orderQty' | 'closedShort'>) =>
  order.closedShort ? 0 : Math.max(0, num(order.orderQty) - orderReceivedQty(order));

export type DeliveryDocumentType = 'challan' | 'bill';
export type DeliveryLine = { orderId: string; qty: number; boxes?: number; piecesPerBox?: number; areaPerPiece?: number };
export type DeliveryMeta = {
  date: string; reference: string; notes: string; documentType: DeliveryDocumentType;
  photo?: { url: string; path: string };
  /** Which of the vendor's regular transporters actually delivered this load. */
  cartingAgent?: string;
};

/**
 * Receipt fields for a delivery. A bill number noted at the gate is kept on the receipt but stays
 * unbilled (billDetailsPending) until accounts add the rates and totals.
 */
export function deliveryReceiptFields(meta: Pick<DeliveryMeta, 'reference' | 'notes' | 'documentType' | 'photo' | 'cartingAgent'>) {
  return {
    reference: meta.reference,
    notes: meta.notes,
    documentType: meta.documentType,
    ...(meta.cartingAgent?.trim() ? { cartingAgent: meta.cartingAgent.trim() } : {}),
    ...(meta.documentType === 'bill' ? { billNumber: meta.reference, billDetailsPending: true, gateBill: true } : {}),
    ...(meta.photo ? { photoUrl: meta.photo.url, photoPath: meta.photo.path } : {}),
  };
}

// ── What is waiting to be received ───────────────────────────────────────────
export type ReceivableOrder = MaterialProcurementOrder & { pending: number };

const normLabel = (value?: string) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
const isOpenLine = (order: ReceivableOrder) => order.pending > 0 && !order.unplannedRejected;

export type OpenVendor<T> = { key: string; label: string; lines: T[] };

/** Vendors that have at least one open line, with those lines. */
export function openVendors<T extends ReceivableOrder>(orders: T[]): Array<OpenVendor<T>> {
  const map = new Map<string, OpenVendor<T>>();
  for (const order of orders) {
    if (!isOpenLine(order)) continue;
    const key = normVendor(order.vendor);
    const entry = map.get(key) || { key, label: order.vendor?.trim() || 'Vendor not set', lines: [] };
    entry.lines.push(order);
    map.set(key, entry);
  }
  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
}

export const groupLabel = (value: string) =>
  String(value || '').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[-_]/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

export type OpenMaterial<T> = { key: string; label: string; /** Full name, for searching. */ search: string; unit: string; pending: number; lines: T[] };

/** Materials with open lines, grouped by material group (Cement, Steel...) and sorted. */
export function openMaterialsByGroup<T extends ReceivableOrder>(orders: T[], items: MaterialRateItem[]): Array<{ group: string; materials: Array<OpenMaterial<T>> }> {
  const groups = new Map<string, Map<string, OpenMaterial<T>>>();
  for (const order of orders) {
    if (!isOpenLine(order)) continue;
    const item = order.materialId ? items.find((candidate) => candidate.id === order.materialId) : undefined;
    const group = item ? groupLabel(String(item.group)) : 'Other';
    const key = order.materialId ? `${order.materialId}|${order.quoteOptionId || ''}` : `label|${normLabel(order.materialLabel)}`;
    const materials = groups.get(group) || new Map<string, OpenMaterial<T>>();
    const entry = materials.get(key) || { key, label: (order as { displayTitle?: string }).displayTitle || order.materialLabel, search: order.materialLabel, unit: order.unit, pending: 0, lines: [] };
    entry.pending += order.pending;
    entry.lines.push(order);
    materials.set(key, entry);
    groups.set(group, materials);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => (a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b)))
    .map(([group, materials]) => ({ group, materials: Array.from(materials.values()).sort((a, b) => a.label.localeCompare(b.label)) }));
}

/** Open PO lines for the same material, used to warn before a delivery is recorded without a PO. */
export function findOpenPoLines<T extends ReceivableOrder>(orders: T[], target: { materialId?: string; quoteOptionId?: string; materialLabel: string }): T[] {
  return orders.filter((order) => {
    if (!isOpenLine(order)) return false;
    if (target.materialId && order.materialId) {
      return order.materialId === target.materialId && (!target.quoteOptionId || !order.quoteOptionId || order.quoteOptionId === target.quoteOptionId);
    }
    return normLabel(order.materialLabel) === normLabel(target.materialLabel);
  });
}

// ── Material picker for deliveries that have no PO ───────────────────────────
export type MaterialPick = { key: string; item: MaterialRateItem; optionId: string; assemblyType: string; variety: string; label: string; unit: string };

const assemblyMappingUnit = (item: MaterialRateItem, assemblyType: string, variety: string) =>
  (item.assemblyVarietyMap || []).find((entry) =>
    samePickText(entry.assemblyType, assemblyType)
    && (!variety || (entry.varieties || []).some((value) => samePickText(value, variety)))
  )?.unit || '';

const samePickText = (a?: string, b?: string) => !!String(a || '').trim() && normLabel(a) === normLabel(b);
const optionMatchesAssemblyVariety = (option: Option, assemblyType: string, variety: string) => {
  const assembly = String(assemblyType || '').trim();
  const pickedVariety = String(variety || '').trim();
  const groupOrNameMatchesAssembly = !assembly || samePickText(option.group, assembly) || samePickText(option.name, assembly);
  if (pickedVariety) {
    return groupOrNameMatchesAssembly && (samePickText(option.variety, pickedVariety) || samePickText(option.name, pickedVariety));
  }
  return !!assembly && (samePickText(option.group, assembly) || samePickText(option.variety, assembly) || samePickText(option.name, assembly));
};

export function libraryPicks(materialItems: MaterialRateItem[]): MaterialPick[] {
  const optionFor = (item: MaterialRateItem, assemblyType: string, variety: string) =>
    (item.quotes || []).flatMap((quote) => quote.quoteOptions || []).find((option) => optionMatchesAssemblyVariety(option, assemblyType, variety)) || null;
  return materialItems
    .filter((item) => item.group !== 'labour')
    .flatMap((item) => {
      const mapped = (item.assemblyVarietyMap || []).flatMap((entry) => {
        const varieties = entry.varieties?.length ? entry.varieties : [''];
        return varieties.map((variety) => ({ assemblyType: entry.assemblyType || item.materialName, variety }));
      });
      const entries = mapped.length ? mapped : (() => {
        const assemblyTypes = item.assemblyTypes?.length ? item.assemblyTypes : [item.materialName];
        const varieties = item.varieties?.length ? item.varieties : [''];
        return assemblyTypes.flatMap((assemblyType) => varieties.map((variety) => ({ assemblyType, variety })));
      })();
      return entries.map(({ assemblyType, variety }) => {
        const option = optionFor(item, assemblyType, variety);
        return {
          key: `${item.id}_${assemblyType || 'material'}_${variety || 'base'}`,
          item, optionId: option?.id || '', assemblyType, variety,
          label: [groupLabel(String(item.group)), assemblyType, variety].filter(Boolean).join(' · '),
          // The assembly/type is the material definition. A historic vendor quote must not
          // replace its unit when the material was corrected later.
          unit: assemblyMappingUnit(item, assemblyType, variety) || option?.unit || item.unit || 'Nos',
        };
      });
    });
}

/**
 * Library materials matching the search text that have no open PO, so a delivery can still be
 * recorded for them. The whole name (group, assembly, variety) is searched.
 */
export function notOnOrderPicks<T extends ReceivableOrder>(picks: MaterialPick[], orders: T[], needle: string): MaterialPick[] {
  const text = needle.trim().toLowerCase();
  if (!text) return [];
  return picks
    .filter((pick) => `${pick.label} ${pick.item.materialName}`.toLowerCase().includes(text))
    .filter((pick) => findOpenPoLines(orders, { materialId: pick.item.id, quoteOptionId: pick.optionId, materialLabel: pick.label }).length === 0)
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The name to show for an order line. A line saved a name when it was created; renaming a group,
 * material or variety in the library must show everywhere, old lines included, so the name is worked
 * out again from the library. `catalogLabels` maps `materialId|quoteOptionId` to the name the order
 * catalog gives that option. A line that cannot be found in the library keeps the name it was saved with.
 */
export function liveOrderLabel(
  order: Pick<MaterialProcurementOrder, 'materialId' | 'quoteOptionId' | 'materialLabel'> & { materialAssemblyType?: string; materialVariety?: string },
  picks: MaterialPick[],
  catalogLabels: Map<string, string>,
): string {
  const saved = order.materialLabel;
  const same = (a?: string, b?: string) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
  const parts = String(saved || '').split('·').map((part) => part.trim()).filter(Boolean);
  const fromName = parts.length >= 3 ? { assemblyType: parts[parts.length - 2], variety: parts[parts.length - 1] } : null;
  if (!order.materialId) {
    // Not linked to a library material: follow a rename only when the assembly and variety identify exactly one.
    const matches = fromName ? picks.filter((candidate) => same(candidate.variety, fromName.variety) && same(candidate.assemblyType, fromName.assemblyType)) : [];
    return matches.length === 1 ? matches[0].label : saved;
  }
  const wanted = order.materialVariety || order.materialAssemblyType
    ? { assemblyType: order.materialAssemblyType || '', variety: order.materialVariety || '' }
    : fromName;
  if (wanted) {
    const pick = picks.find((candidate) => candidate.item.id === order.materialId && same(candidate.variety, wanted.variety) && same(candidate.assemblyType, wanted.assemblyType));
    if (pick) return pick.label;
  }
  return catalogLabels.get(`${order.materialId}|${order.quoteOptionId || ''}`) || saved;
}

// ── How a material is named on screen ────────────────────────────────────────
/** `title` is assembly · variety (once if they match); `group` is shown small and gray under it. */
export type MaterialName = { title: string; group: string };

const joinDistinct = (parts: Array<string | undefined>) =>
  parts.map((part) => String(part || '').trim()).filter(Boolean)
    .filter((part, index, all) => index === 0 || part.toLowerCase() !== all[index - 1].toLowerCase())
    .join(' · ');

export function materialNameOf(item: MaterialRateItem | undefined, parts: { assemblyType?: string; variety?: string; name?: string }, fallback: string): MaterialName {
  return {
    title: joinDistinct([parts.assemblyType, parts.variety, parts.name]) || item?.materialName || fallback,
    group: item ? groupLabel(String(item.group)) : '',
  };
}

export const pickName = (pick: MaterialPick): MaterialName => materialNameOf(pick.item, { assemblyType: pick.assemblyType, variety: pick.variety }, pick.label);

/**
 * The on-screen name of an order line, worked out from the library so it follows renames. A line that
 * is not linked to a library material keeps the name it was saved with and has no group line.
 */
export function orderName(
  order: Pick<MaterialProcurementOrder, 'materialId' | 'quoteOptionId' | 'materialLabel'> & { materialAssemblyType?: string; materialVariety?: string },
  items: MaterialRateItem[],
): MaterialName {
  const saved = order.materialLabel;
  const item = order.materialId ? items.find((candidate) => candidate.id === order.materialId) : undefined;
  if (!item) return { title: saved, group: '' };
  const option = order.quoteOptionId ? (item.quotes || []).flatMap((quote) => quote.quoteOptions || []).find((candidate) => candidate.id === order.quoteOptionId) : undefined;
  if (option) return materialNameOf(item, { assemblyType: option.group, variety: option.variety, name: option.name }, saved);
  if (order.materialVariety || order.materialAssemblyType) return materialNameOf(item, { assemblyType: order.materialAssemblyType, variety: order.materialVariety }, saved);
  const parts = String(saved || '').split('·').map((part) => part.trim()).filter(Boolean);
  // The saved name starts with the group or material name, which is shown separately.
  return parts.length >= 2 ? { title: joinDistinct(parts.slice(1)) || saved, group: groupLabel(String(item.group)) } : materialNameOf(item, {}, saved);
}

/** What screens show for an order line; falls back to the saved name when nothing was worked out. */
export const orderTitle = (order: { materialLabel: string; displayTitle?: string }) => order.displayTitle || order.materialLabel;
export const orderGroup = (order: { displayGroup?: string }) => order.displayGroup || '';

/** The library pick that corresponds to an order line, if it came from the library. */
export function pickForOrder(picks: MaterialPick[], order: Pick<MaterialProcurementOrder, 'materialId' | 'quoteOptionId'>): MaterialPick | null {
  if (!order.materialId) return null;
  return picks.find((pick) => pick.item.id === order.materialId && (pick.optionId || '') === (order.quoteOptionId || ''))
    || picks.find((pick) => pick.item.id === order.materialId)
    || null;
}

/**
 * Materials actually received before, newest delivery first and deduplicated — a quick way to start a
 * no-PO delivery for something routinely received (cement, sand) without searching for it each time.
 * Independent of whether it currently has an open order: tapping one still shows the usual "there is an
 * open PO" warning if one exists.
 */
export function recentlyReceivedPicks<T extends Pick<MaterialProcurementOrder, 'materialId' | 'quoteOptionId' | 'receipts' | 'unplannedRejected'>>(
  orders: T[], picks: MaterialPick[], limit = 12,
): MaterialPick[] {
  const deliveries = orders
    .filter((order) => !order.unplannedRejected)
    .flatMap((order) => (order.receipts || []).map((receipt) => ({ order, date: String(receipt.date || '') })))
    .sort((a, b) => b.date.localeCompare(a.date));
  const seen = new Set<string>();
  const result: MaterialPick[] = [];
  for (const { order } of deliveries) {
    const pick = pickForOrder(picks, order);
    if (!pick || seen.has(pick.key)) continue;
    seen.add(pick.key);
    result.push(pick);
    if (result.length >= limit) break;
  }
  return result;
}

// ── Vendors for a delivery entered without a PO ──────────────────────────────
export const vendorKey = (value?: string) => normVendor(value).replace(/[^a-z0-9]/g, '');

/** True when two vendor names differ only by case, spacing or punctuation. */
export const sameVendorName = (a?: string, b?: string) => !!vendorKey(a) && vendorKey(a) === vendorKey(b);

export type VendorSuggestion = { name: string; hint: string };

/**
 * Vendors to offer for a material. "Related" are vendors with a quote on the same material or
 * anywhere in its category (material group), plus vendors who supplied that material before. Every
 * other known vendor is returned separately so a new supplier is never blocked.
 */
export function vendorSuggestions<T extends Pick<MaterialProcurementOrder, 'vendor' | 'materialId' | 'materialAssemblyType' | 'materialVariety'>>(
  items: MaterialRateItem[], orders: T[], pick: MaterialPick | null, knownVendors: string[] = [],
): { related: VendorSuggestion[]; others: VendorSuggestion[] } {
  const found = new Map<string, { name: string; rank: number; hint: string }>();
  const consider = (name: unknown, rank: number, hint: string) => {
    const clean = String(name || '').trim();
    const key = vendorKey(clean);
    if (!key) return;
    const current = found.get(key);
    if (!current || rank < current.rank) found.set(key, { name: current?.name || clean, rank, hint });
  };
  const quotesOf = (item: MaterialRateItem) => (item.quotes || []).filter((quote) => (quote.status || 'active') !== 'voided');
  const hasRate = (value: unknown, history: Array<{ rate?: number }> = []) => num(value) > 0 || history.some((entry) => num(entry?.rate) > 0);
  const rateHint = (quote: NonNullable<MaterialRateItem['quotes']>[number]) => {
    const option = (quote.quoteOptions || []).find((candidate) =>
      (pick?.optionId && candidate.id === pick.optionId)
      || (!!pick && optionMatchesAssemblyVariety(candidate, pick.assemblyType, pick.variety)));
    return (option ? hasRate(option.rate, option.rateHistory) : hasRate(quote.rate, quote.baseRateHistory)) ? 'Saved rate' : 'No rate added';
  };
  if (pick) {
    // A bundled item ("Concrete Materials" covering Sand, Aggregate 10mm, Aggregate 20mm as separate
    // assembly-type rows) shares one item id across all of them — without this, a vendor who only ever
    // supplied Sand would be suggested for an Aggregate 10mm delivery too, since both sit on the same item.
    const bundled = (pick.item.assemblyVarietyMap || []).length > 1;
    const matchesPick = (assemblyType?: string, variety?: string) =>
      !bundled || (samePickText(assemblyType, pick.assemblyType) && (!pick.variety || samePickText(variety, pick.variety)));
    quotesOf(pick.item)
      .filter((quote) => !bundled || (quote.quoteOptions || []).some((option) => optionMatchesAssemblyVariety(option, pick.assemblyType, pick.variety)))
      .forEach((quote) => consider(quote.vendor, 0, rateHint(quote)));
    orders.filter((order) => order.materialId === pick.item.id && matchesPick(order.materialAssemblyType, order.materialVariety))
      .forEach((order) => consider(order.vendor, 1, 'Supplied before'));
    // Skipped for a bundled row: "same category, different item" is too loose once one item can already
    // hold several unrelated commodities (Sand, Aggregate 10mm, Aggregate 20mm) under one shared id.
    if (!bundled) {
      items.filter((item) => item.id !== pick.item.id && item.group === pick.item.group)
        .forEach((item) => quotesOf(item).forEach((quote) => consider(quote.vendor, 2, 'In this category')));
    }
  }
  items.forEach((item) => quotesOf(item).forEach((quote) => consider(quote.vendor, 3, '')));
  orders.forEach((order) => consider(order.vendor, 3, ''));
  // Vendors are shared by every project, so a vendor added in another project is offered here too.
  knownVendors.forEach((name) => consider(name, 3, ''));
  const all = Array.from(found.values()).sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name));
  const toSuggestion = (entry: { name: string; hint: string }) => ({ name: entry.name, hint: entry.hint });
  return { related: all.filter((entry) => entry.rank < 3).map(toSuggestion), others: all.filter((entry) => entry.rank === 3).map(toSuggestion) };
}

/**
 * Every known vendor, with the ones already tied to this material (a quote on it, or a past delivery)
 * listed first — for a vendor field that picks a whole material item rather than one specific
 * assembly-type/variety row (adding a quote, or editing an already-placed order's vendor), so every
 * vendor stays reachable but the ones that actually matter here don't get buried in a project-wide list.
 */
export function vendorOptionsForItem(
  item: Pick<MaterialRateItem, 'id' | 'quotes'>,
  orders: Array<{ materialId?: string; vendor?: string }>,
  knownVendors: string[] = [],
): string[] {
  const related = new Set<string>();
  (item.quotes || []).forEach((quote) => { if ((quote.status || 'active') !== 'voided' && quote.vendor?.trim()) related.add(quote.vendor.trim()); });
  orders.filter((order) => order.materialId === item.id).forEach((order) => { if (order.vendor?.trim()) related.add(order.vendor.trim()); });
  const rest = new Set<string>([...orders.map((order) => order.vendor?.trim() || ''), ...knownVendors.map((name) => name.trim())].filter(Boolean));
  related.forEach((name) => rest.delete(name));
  const sortText = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });
  return [...Array.from(related).sort(sortText), ...Array.from(rest).sort(sortText)];
}

/**
 * True when a delivery's vendor has no (non-voided) quote for the material in the rate library,
 * so purchasing should be asked to add one. Needs a library material and a vendor name.
 */
export function needsVendorQuote(item: MaterialRateItem | undefined, vendor?: string): boolean {
  if (!item || !String(vendor || '').trim()) return false;
  return !(item.quotes || []).some((quote) => (quote.status || 'active') !== 'voided' && sameVendorName(quote.vendor, vendor));
}

export const roundMoney = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

// ── Locating the library rate an order refers to ─────────────────────────────
export function findQuoteForOrder(item: MaterialRateItem | undefined, order: Pick<MaterialProcurementOrder, 'quoteId' | 'vendor'>): Quote | null {
  const quotes = (item?.quotes || []).filter((quote) => (quote.status || 'active') !== 'voided');
  if (!quotes.length) return null;
  return quotes.find((quote) => quote.id === order.quoteId)
    || quotes.find((quote) => !!normVendor(quote.vendor) && normVendor(quote.vendor) === normVendor(order.vendor))
    || null;
}

export function findOptionForOrder(quote: Quote | null, order: Pick<MaterialProcurementOrder, 'quoteOptionId' | 'materialLabel'>): Option | null {
  if (!quote) return null;
  const options = quote.quoteOptions || [];
  if (order.quoteOptionId) return options.find((option) => option.id === order.quoteOptionId) || null;
  const label = String(order.materialLabel || '').toLowerCase();
  const parts = label.split('·').map((part) => part.trim()).filter(Boolean);
  const matches = (value?: string) => {
    const text = String(value || '').trim().toLowerCase();
    return !!text && (parts.includes(text) || label.includes(text));
  };
  return options.find((option) => matches(option.variety) || matches(option.name) || matches(option.group)) || null;
}

// ── Pack sizes ("50 Kg" style varieties) ─────────────────────────────────────
const PACK_PATTERN = /^(\d+(?:\.\d+)?)\s*(kg|kgs|bag|bags|box|boxes|set|sets|roll|rolls|nos?)$/i;

/** Pack quantity in the order unit when the variety is a pack label, else 0. */
function packQty(variety: string | undefined, orderUnit: string): number {
  const match = PACK_PATTERN.exec(String(variety || '').trim());
  if (!match) return 0;
  const qty = num(match[1]);
  return qty > 1 && sameUnit(match[2], orderUnit) ? qty : 0;
}

function varietyFor(order: Pick<MaterialProcurementOrder, 'materialLabel'>, option: Option | null): string {
  if (option?.variety) return option.variety;
  const parts = String(order.materialLabel || '').split('·').map((part) => part.trim()).filter(Boolean);
  return parts[parts.length - 1] || '';
}

/** Multiply a rate per library unit by this to get a rate per order unit. */
export function libraryUnitToOrderUnit(order: MaterialProcurementOrder, option: Option | null): number {
  const pack = packQty(varietyFor(order, option), order.unit);
  return pack > 0 ? 1 / pack : 1;
}

// ── Library / PO rates ───────────────────────────────────────────────────────
export const quoteIsGstInclusive = (quote: Quote) => quote.gstMode === 'inclusive' || quote.gstMode === 'none';

/** GST-inclusive rate per order unit currently in the library for the order's vendor quote (0 if none). */
export function libraryRatePerOrderUnit(item: MaterialRateItem | undefined, order: MaterialProcurementOrder, date: string): number {
  const quote = findQuoteForOrder(item, order);
  if (!quote) return 0;
  const option = findOptionForOrder(quote, order);
  const base = option ? quoteOptionBaseRate(quote, option, date) : quoteBaseRate(quote, date);
  if (base <= 0) return 0;
  const incl = quoteIsGstInclusive(quote) ? base : base * (1 + num(quote.gst) / 100);
  return roundMoney(incl * libraryUnitToOrderUnit(order, option));
}

/** GST-inclusive, freight-free rate per order unit captured on the PO (0 if the PO has no snapshot). */
export function poRatePerOrderUnit(item: MaterialRateItem | undefined, order: MaterialProcurementOrder): number {
  const orderedInclRate = num(order.orderedRateInclGst);
  if (orderedInclRate > 0) {
    const quote = findQuoteForOrder(item, order);
    return roundMoney(orderedInclRate * libraryUnitToOrderUnit(order, quote ? findOptionForOrder(quote, order) : null));
  }
  const snapshot = num(order.rateSnapshot);
  if (snapshot <= 0) return 0;
  const quote = findQuoteForOrder(item, order);
  // rateSnapshot was saved as a landed rate (GST + freight + loading); strip freight and loading.
  const landedExtras = quote ? num(quote.freight) + num(quote.loading) : 0;
  const incl = Math.max(0, snapshot - landedExtras);
  return roundMoney(incl * libraryUnitToOrderUnit(order, quote ? findOptionForOrder(quote, order) : null));
}

// ── Rate unit choices for the bill form ──────────────────────────────────────
export type RateUnitChoice = { key: string; label: string; mult: number }; // perOrderUnit = entered * mult

const boxPackingForOrder = (
  item: MaterialRateItem | undefined,
  order: Pick<MaterialProcurementOrder, 'materialLabel' | 'materialAssemblyType' | 'materialVariety' | 'unit'>,
  option: Option | null,
) => {
  const packings = item?.boxPackings || [];
  if (!packings.length) return null;
  const parts = String(order.materialLabel || '').split('·').map((part) => part.trim()).filter(Boolean);
  const assemblyCandidates = [order.materialAssemblyType, option?.group, parts.length > 1 ? parts[parts.length - 2] : '', option?.name].filter(Boolean);
  const varietyCandidates = [order.materialVariety, option?.variety, option?.name, parts[parts.length - 1]].filter(Boolean);
  return packings.find((packing) =>
    assemblyCandidates.some((assembly) => samePickText(packing.assemblyType, assembly))
    && varietyCandidates.some((variety) => samePickText(packing.variety, variety))
  ) || (packings.length === 1 && parts.some((part) => samePickText(packings[0].assemblyType, part) || samePickText(packings[0].variety, part)) ? packings[0] : null);
};

export function rateUnitChoices(item: MaterialRateItem | undefined, order: MaterialProcurementOrder): RateUnitChoice[] {
  const quote = findQuoteForOrder(item, order);
  const option = findOptionForOrder(quote, order);
  const choices: RateUnitChoice[] = [{ key: 'unit', label: order.unit || 'unit', mult: 1 }];
  const source = option?.secondaryUnitRequired ? option : item;
  const multiplier = num(source?.secondaryMultiplier);
  const secondaryUnit = String(source?.secondaryUnit || '').trim();
  if (source?.secondaryUnitRequired && multiplier > 0 && secondaryUnit) {
    if (sameUnit(order.unit, source.unit) && !sameUnit(source.unit, secondaryUnit)) {
      // 1 primary = m secondary, so a rate per secondary unit is m times smaller.
      choices.push({ key: 'secondary', label: secondaryUnit, mult: multiplier });
    } else if (sameUnit(order.unit, secondaryUnit) && source.unit) {
      choices.push({ key: 'secondary', label: source.unit, mult: 1 / multiplier });
    }
  }
  const variety = varietyFor(order, option);
  const pack = packQty(variety, order.unit);
  if (pack > 0) choices.push({ key: 'pack', label: variety, mult: 1 / pack });
  const boxPacking = boxPackingForOrder(item, order, option);
  const boxQty = num(boxPacking?.piecesPerBox) * num(boxPacking?.areaPerPiece);
  if (boxQty > 0) choices.push({ key: 'box', label: 'Box', mult: 1 / boxQty });
  const seen = new Set<string>();
  return choices.filter((choice) => {
    const id = normalizeUnit(choice.label);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/** The unit the library itself quotes this rate in (pack for pack-labelled varieties). */
export function defaultRateUnitKey(choices: RateUnitChoice[], order: MaterialProcurementOrder, item: MaterialRateItem | undefined): string {
  const quote = findQuoteForOrder(item, order);
  const pack = packQty(varietyFor(order, findOptionForOrder(quote, order)), order.unit);
  return pack > 0 && choices.some((choice) => choice.key === 'pack') ? 'pack' : choices[0].key;
}

// ── Rate prefill for one bill line ───────────────────────────────────────────
export type BillRatePrefill = {
  perOrderUnit: number;      // GST-inclusive rate per order unit to start from (0 = none found)
  libraryPerOrderUnit: number;
  poPerOrderUnit: number;
  sourceLabel: string;
  quoteFound: boolean;
};

export function billRatePrefill(item: MaterialRateItem | undefined, order: MaterialProcurementOrder, date: string): BillRatePrefill {
  const quote = findQuoteForOrder(item, order);
  const poPerOrderUnit = poRatePerOrderUnit(item, order);
  const libraryPerOrderUnit = libraryRatePerOrderUnit(item, order, date);
  if (poPerOrderUnit > 0) return { perOrderUnit: poPerOrderUnit, libraryPerOrderUnit, poPerOrderUnit, sourceLabel: 'Ordered rate', quoteFound: !!quote };
  if (libraryPerOrderUnit > 0) return { perOrderUnit: libraryPerOrderUnit, libraryPerOrderUnit, poPerOrderUnit, sourceLabel: `Rate library · ${quote?.vendor || 'vendor'} quote`, quoteFound: !!quote };
  return { perOrderUnit: 0, libraryPerOrderUnit: 0, poPerOrderUnit: 0, sourceLabel: quote ? 'No rate in the library for this variety' : `No ${order.vendor || 'vendor'} quote in the rate library`, quoteFound: !!quote };
}

// ── Updating the library from a bill (opt-in) ────────────────────────────────
/**
 * Returns the item's quotes with a new revision (effective from the bill date) for the order's
 * vendor quote/option, or null when the vendor quote or option cannot be found.
 */
export function quotesWithBillRevision(
  item: MaterialRateItem,
  order: MaterialProcurementOrder,
  billPerOrderUnit: number,
  billDate: string,
  billNumber: string,
): NonNullable<MaterialRateItem['quotes']> | null {
  const quote = findQuoteForOrder(item, order);
  if (!quote || billPerOrderUnit <= 0) return null;
  const option = findOptionForOrder(quote, order);
  if (order.quoteOptionId && !option) return null;
  const perLibraryUnitIncl = billPerOrderUnit / libraryUnitToOrderUnit(order, option);
  const newBase = roundMoney(quoteIsGstInclusive(quote) ? perLibraryUnitIncl : perLibraryUnitIncl / (1 + num(quote.gst) / 100));
  const previousBase = option ? quoteOptionBaseRate(quote, option, billDate) : quoteBaseRate(quote, billDate);
  if (Math.abs(newBase - previousBase) < 0.005) return null;
  const revision = {
    id: `bill_rate_${Date.now()}_${order.id}`,
    previousRate: previousBase,
    rate: newBase,
    effectiveFrom: billDate,
    reason: `Vendor bill ${billNumber}`,
    recordedAt: new Date().toISOString(),
  };
  return (item.quotes || []).map((candidate) => {
    if (candidate.id !== quote.id) return candidate;
    if (!option) return { ...candidate, baseRateHistory: [...(candidate.baseRateHistory || []), revision] };
    return {
      ...candidate,
      quoteOptions: (candidate.quoteOptions || []).map((entry) => entry.id === option.id ? { ...entry, rateHistory: [...(entry.rateHistory || []), revision] } : entry),
    };
  });
}

// ── Capturing a rate while placing an order ──────────────────────────────────
export type OrderPriceEntry = { vendor: string; rate: string; gstMode: 'inclusive' | 'exclusive' };

/**
 * Turns a rate typed while placing an order into the quote change to save: a brand-new quote for a
 * vendor who has never quoted this material, a first rate on an option they had never priced, or a
 * dated revision to a price they already had. `entry.rate` is per the order's own unit (what the
 * screen shows next to the field), and the toggle says whether it includes GST.
 * Returns null when there is nothing to save (blank or non-positive rate, or the material has no
 * option to price into for a variety that needs one).
 */
export function planOrderPriceEntry(
  item: MaterialRateItem,
  optionId: string | undefined,
  order: { unit: string; materialLabel: string },
  entry: OrderPriceEntry,
  effectiveFrom: string,
  reason: string,
): NonNullable<MaterialRateItem['quotes']> | null {
  const typed = num(entry.rate);
  const vendor = entry.vendor.trim();
  if (typed <= 0 || !vendor) return null;

  const quotes = item.quotes || [];
  const existing = quotes.find((quote) => (quote.status || 'active') !== 'voided' && sameVendorName(quote.vendor, vendor));
  const anyActive = quotes.find((quote) => (quote.status || 'active') !== 'voided');
  const pseudoOrder = { unit: order.unit, materialLabel: order.materialLabel } as MaterialProcurementOrder;

  // The option to price into: the vendor's own quote if it already has one, else any other vendor's
  // (option ids are shared across vendors), else the material's own templates.
  const optionShape = (): Option | null => {
    if (!optionId) return null;
    const fromExisting = existing?.quoteOptions?.find((option) => option.id === optionId);
    if (fromExisting) return fromExisting;
    for (const quote of quotes) {
      const found = quote.quoteOptions?.find((option) => option.id === optionId);
      if (found) return { ...found, rate: 0, rateHistory: [] };
    }
    const templated = expandQuoteOptionsFromTemplate(item, []).find((option) => option.id === optionId);
    return templated ? { ...templated, rate: 0, rateHistory: [] } : null;
  };
  const option = optionShape();
  if (optionId && !option) return null; // this material has no such option to price into

  const unitFactor = libraryUnitToOrderUnit(pseudoOrder, option);
  const perLibraryUnit = typed / unitFactor;

  if (!existing) {
    // A vendor who has never quoted this material: gst% is guessed from another active quote, else 18%.
    const gst = anyActive ? num(anyActive.gst) || 18 : 18;
    const quote: Quote = {
      id: `order_quote_${Date.now()}`, vendor, brand: '', rate: option ? 0 : roundMoney(perLibraryUnit),
      unit: item.unit, gst, gstMode: entry.gstMode, status: 'active', quoteDate: effectiveFrom, validUntil: '',
      quoteOptions: option ? [{ ...option, rate: roundMoney(perLibraryUnit) }] : [],
    } as Quote;
    return [...quotes, quote];
  }

  // This vendor already has a quote: gstMode is fixed on it, so the typed value is converted to match.
  const toInclusive = (value: number) => entry.gstMode === 'inclusive' ? value : value * (1 + num(existing.gst) / 100);
  const toExistingBase = (value: number) => quoteIsGstInclusive(existing) ? toInclusive(value) : toInclusive(value) / (1 + num(existing.gst) / 100);
  const newBase = roundMoney(toExistingBase(perLibraryUnit));

  return quotes.map((quote) => {
    if (quote.id !== existing.id) return quote;
    if (!option) {
      const previousBase = quoteBaseRate(quote, effectiveFrom);
      if (previousBase <= 0) return { ...quote, rate: newBase };
      if (Math.abs(newBase - previousBase) < 0.005) return quote;
      return { ...quote, baseRateHistory: [...(quote.baseRateHistory || []), { id: `order_rate_${Date.now()}`, previousRate: previousBase, rate: newBase, effectiveFrom, reason, recordedAt: new Date().toISOString() }] };
    }
    const already = quote.quoteOptions?.some((candidate) => candidate.id === option.id && num(candidate.rate) > 0);
    const previousBase = already ? quoteOptionBaseRate(quote, option, effectiveFrom) : 0;
    if (!already) {
      const hasOption = quote.quoteOptions?.some((candidate) => candidate.id === option.id);
      const nextOptions = hasOption
        ? (quote.quoteOptions || []).map((candidate) => candidate.id === option.id ? { ...candidate, rate: newBase } : candidate)
        : [...(quote.quoteOptions || []), { ...option, rate: newBase }];
      return { ...quote, quoteOptions: nextOptions };
    }
    if (Math.abs(newBase - previousBase) < 0.005) return quote;
    return {
      ...quote,
      quoteOptions: (quote.quoteOptions || []).map((candidate) => candidate.id === option.id
        ? { ...candidate, rateHistory: [...(candidate.rateHistory || []), { id: `order_rate_${Date.now()}`, previousRate: previousBase, rate: newBase, effectiveFrom, reason, recordedAt: new Date().toISOString() }] }
        : candidate),
    };
  });
}
